import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Section from './shared/Section';

/* ── Floating label input ── */
const FloatingInput = ({ label, type = 'text', required = false, name, resetCounter }) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  const isActive = focused || value.length > 0;

  useEffect(() => {
    if (typeof resetCounter === 'number') {
      setValue('');
      setFocused(false);
    }
  }, [resetCounter]);

  return (
    <div className="relative">
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        className="peer w-full px-4 pt-6 pb-2 bg-dark border border-dark-border rounded-xl text-white outline-none focus:border-primary transition-colors duration-300"
        aria-label={label}
      />
      <motion.label
        className="absolute left-4 pointer-events-none text-gray-500 origin-left"
        animate={{
          top: isActive ? 8 : 16,
          fontSize: isActive ? '11px' : '14px',
          color: focused ? '#08CB00' : '#6b7280',
        }}
        transition={{ duration: 0.2 }}
      >
        {label}{required && <span className="text-primary/50 ml-0.5">*</span>}
      </motion.label>
    </div>
  );
};

/* ── Floating label textarea ── */
const FloatingTextarea = ({ label, required = false, name, resetCounter }) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  const isActive = focused || value.length > 0;

  useEffect(() => {
    if (typeof resetCounter === 'number') {
      setValue('');
      setFocused(false);
    }
  }, [resetCounter]);

  return (
    <div className="relative">
      <textarea
        name={name}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        rows={4}
        className="peer w-full px-4 pt-6 pb-2 bg-dark border border-dark-border rounded-xl text-white outline-none focus:border-primary transition-colors duration-300 resize-none"
        aria-label={label}
      />
      <motion.label
        className="absolute left-4 pointer-events-none text-gray-500 origin-left"
        animate={{
          top: isActive ? 8 : 16,
          fontSize: isActive ? '11px' : '14px',
          color: focused ? '#08CB00' : '#6b7280',
        }}
        transition={{ duration: 0.2 }}
      >
        {label}
      </motion.label>
    </div>
  );
};

/* ── Map placeholder with glowing pins ── */
const MapPlaceholder = () => (
  <div className="relative h-full min-h-[320px] lg:min-h-0 bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
    {/* Grid pattern */}
    <div className="absolute inset-0 grid-pattern opacity-50" />
    {/* Stylised map SVG */}
    <svg className="absolute inset-0 w-full h-full opacity-[0.12]" viewBox="0 0 400 300" fill="none" stroke="currentColor" strokeWidth="0.8">
      <path d="M60 90 C80 60,140 50,150 80 C160 60,200 70,190 90 C200 110,140 140,110 130 C80 140,50 120,60 90Z" className="stroke-primary/40" />
      <path d="M210 70 C240 40,360 40,380 80 C390 120,360 150,330 140 C310 160,270 150,260 130 C240 120,200 100,210 70Z" className="stroke-primary/40" />
      <path d="M100 160 C120 140,170 150,160 190 C150 220,120 230,100 210 C80 200,90 180,100 160Z" className="stroke-primary/40" />
      <path d="M230 160 C250 140,290 150,280 190 C270 230,240 220,230 200 C220 190,220 180,230 160Z" className="stroke-primary/40" />
      <path d="M340 120 C370 110,400 140,390 170 C380 190,350 180,340 160 C330 150,330 140,340 120Z" className="stroke-primary/40" />
    </svg>
    {/* Glowing pins */}
    {[
      { x: '28%', y: '32%', label: 'San Francisco, CA', primary: true },
      { x: '72%', y: '28%', label: 'London, UK' },
      { x: '52%', y: '55%', label: 'Singapore' },
    ].map((pin, i) => (
      <div key={i} className="absolute group" style={{ left: pin.x, top: pin.y }}>
        <motion.div
          className={`w-3 h-3 rounded-full ${pin.primary ? 'bg-primary' : 'bg-secondary/60'}`}
          animate={{
            boxShadow: [
              `0 0 0 0 rgba(8,203,0,${pin.primary ? 0.4 : 0.2})`,
              `0 0 0 12px rgba(8,203,0,0)`,
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.7 }}
        />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-dark-card border border-dark-border rounded text-[10px] text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          {pin.label}
        </div>
      </div>
    ))}
    {/* Center label */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center">
        <div className="text-gray-600 text-sm font-medium mb-1">Global Headquarters</div>
        <div className="text-xs text-gray-700">San Francisco, CA</div>
      </div>
    </div>
  </div>
);

/* ── Main ── */
const Contact = () => {
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [resetCounter, setResetCounter] = useState(0);
  const turnstileRef = useRef(null);
  const [tcWidgetId, setTcWidgetId] = useState(null);
  const [tcToken, setTcToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    setStatus('sending');
    const data = new FormData(form);

    // Ensure Turnstile token is present
    let token = tcToken;
    if (!token && tcWidgetId != null && window.turnstile) {
      try { token = window.turnstile.getResponse(tcWidgetId); } catch (err) { token = ''; }
    }
    if (!token) {
      setStatus('error');
      return;
    }
    data.append('cf-turnstile-response', token);

    try {
      const res = await fetch(form.action, {
        method: form.method || 'POST',
        body: data,
        headers: {
          Accept: 'application/json',
        },
      });

      if (res.ok) {
        setStatus('success');
        setResetCounter((c) => c + 1);
        // reset turnstile widget and token
        if (window.turnstile && tcWidgetId != null) {
          try { window.turnstile.reset(tcWidgetId); } catch (e) {}
        }
        setTcToken('');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  // Load and render Cloudflare Turnstile
  useEffect(() => {
    const SITE_KEY = '0x4AAAAAACh4pW_0Z6BsgJ4G';

    const renderWidget = () => {
      if (!turnstileRef.current || !window.turnstile) return;
      try {
        const id = window.turnstile.render(turnstileRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => setTcToken(token),
          'expired-callback': () => setTcToken(''),
          'error-callback': () => setTcToken(''),
        });
        setTcWidgetId(id);
      } catch (err) {
        // ignore
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.onload = renderWidget;
    document.head.appendChild(s);

    return () => {
      // cleanup: try to reset widget
      if (window.turnstile && tcWidgetId != null) {
        try { window.turnstile.reset(tcWidgetId); } catch (e) {}
      }
    };
  }, []);

  return (
    <Section id="contact" className="py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="glass-card rounded-3xl p-8 lg:p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" aria-hidden="true" />

        <div className="relative grid lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <div>
            <span className="inline-block px-4 py-1 rounded-full text-xs font-mono tracking-widest uppercase border border-primary/30 text-primary mb-4">
              Get In Touch
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Transform Your <span className="gradient-text">Data Strategy</span>?
            </h2>
            <p className="text-gray-400 leading-relaxed mb-8">
              Let's discuss how OpticVance can accelerate your organization's analytical capabilities.
              Our solutions architects are standing by.
            </p>

            <form
              className="space-y-4"
              action="https://formspree.io/f/xpqjkykn"
              method="POST"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="_subject" value="New contact form submission - OpticVance" />
              <div className="grid sm:grid-cols-2 gap-4">
                <FloatingInput label="First Name" name="firstName" required resetCounter={resetCounter} />
                  <FloatingInput label="Last Name" name="lastName" required resetCounter={resetCounter} />
                </div>
                <FloatingInput label="Work Email" type="email" name="_replyto" required resetCounter={resetCounter} />
                <FloatingInput label="Company" name="company" resetCounter={resetCounter} />
                <FloatingTextarea label="How can we help?" name="message" resetCounter={resetCounter} />

              {/* Turnstile container - widget will render into this element */}
              <div ref={turnstileRef} className="mt-4" />

              <button
                type="submit"
                className="w-full py-4 rounded-xl font-bold text-black transition-all duration-300 glow-btn hover:shadow-lg hover:shadow-primary/20"
                style={{ background: 'linear-gradient(135deg, #08CB00, #54E346)' }}
              >
                Send Message
              </button>
            </form>

              {status === 'sending' && <div className="mt-4 text-sm text-gray-300">Sending...</div>}
              {status === 'success' && <div className="mt-4 text-sm text-green-400">Thanks!! your message has been sent.</div>}
              {status === 'error' && <div className="mt-4 text-sm text-red-400">Sorry, something went wrong. Please try again.</div>}

            {/* Contact info row */}
            <div className="flex flex-wrap gap-6 mt-8">
              {[
                { label: 'Email', value: 'help@opticvance.com' },
                { label: 'Email', value: 'w.clarke@opticvance.com' },
                { label: 'Phone', value: '+5412870652' },
                { label: 'Address', value: '868 Turk St, San Francisco, CA 94102, United States' },
                { label: 'Postal Code', value: '94102' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <span className="text-[10px] font-bold">{item.label.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{item.label}</div>
                    <div className="text-sm">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Map (Google Maps iframe for HQ address) */}
          <div className="flex flex-col">
            <div className="relative h-full min-h-[320px] lg:min-h-0 bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
              <iframe
                title="OpticVance - New York HQ"
                src="https://www.google.com/maps?q=18+Waverly+Pl+New+York+NY+10003&output=embed"
                className="w-full h-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
  );
};

export default Contact;
